import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import AdminHeader from "../../component/AdminHeader";
import { Package, X } from "lucide-react";
import { FormInput } from "../../helpers/AdminHelper";
import BasicProjectInfo from "./AdminProjectComponent/BasicProjectInfo";
import BannerSection from "./AdminProjectComponent/BannerSection";
import ProjectDescription from "./AdminProjectComponent/ProjectDescription";
import BrandProductSection from "./AdminProjectComponent/BrandProductSection";
import FaqSection from "./AdminProjectComponent/FaqSection";
import ServiceSection from "./AdminProjectComponent/ServiceSection";
import VideoSection from "./AdminProjectComponent/VideoSection";
import SeoSection from "./AdminProjectComponent/SeoSection";
import ProjectImageSection from "./AdminProjectComponent/ProjectImageSection";
import { http } from "../../axios/axios";
import { toast } from "react-toastify";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import ActionButtons from "../../component/AdminActionButtons";
import StatCard from "../../component/StatCard";
import Pagination from "../../component/Pagination";

const initialProject = {
  title: "",
  slug: "",
  shortDescription: "",
  description: "",

  bannerImage: "",
  bannerAlt: "",

  category: "",

  clientName: "",

  location: "",

  status: "completed",

  services: [],

  brands: [],

  products: [],

  projectDuration: "",

  challenge: "",

  solution: "",

  result: "",

  gallery: [],

  videos: [],

  faq: [],

  isPublished: true,

  seo: {
    metaTitle: "",
    metaDescription: "",
    metaTags: "",
    schemaScript: "",
  },
};

export default function AdminProjects() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: 30,
  });

  const [project, setProject] = useState(initialProject);

  const [allProjects, setAllProjects] = useState([]);

  const [bannerImage, setBannerImage] = useState(null);
  const initialImages = {
    existing: [],
    new: [],
    removed: [],
  };

  const [images, setImages] = useState(initialImages);
  const [editId, setEditId] = useState(null);

  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);

  const [totalProjectCount, setTotalProjectCount] = useState(null);

  const closeModal = () => {
    images.new.forEach((img) => {
      URL.revokeObjectURL(img.preview);
    });

    setIsModalOpen(false);
    setProject(initialProject);
    setBannerImage(null);
    setImages(initialImages);
    setEditId(null);
  };
  const updateProject = useCallback((field, value) => {
    setProject((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateSeo = useCallback((field, value) => {
    setProject((prev) => ({
      ...prev,
      seo: {
        ...prev.seo,
        [field]: value,
      },
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...project,
      status: project.status?.toLowerCase() || "",

      seo: {
        ...project.seo,
        metaTags: project.seo.metaTags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
      },
    };

    const formData = new FormData();

    formData.append("project", JSON.stringify(payload));

    if (bannerImage) {
      formData.append("bannerImage", bannerImage);
    }

    images.new.forEach((img) => {
      formData.append("galleryImages", img.file);
    });

    formData.append(
      "gallery",
      JSON.stringify(
        images.new.map((img) => ({
          alt: img.alt || "",
          caption: img.caption || "",
        })),
      ),
    );

    formData.append("existingGallery", JSON.stringify(images.existing));

    formData.append("removedGallery", JSON.stringify(images.removed));

    try {
      if (editId) {
        await http.put(`/projects/${editId}`, formData);
        toast.success("Project updated successfully");
      } else {
        await http.post("/projects", formData);
        toast.success("Project created successfully");
      }

      closeModal();
      fetchProjects();
      setEditId(null);
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || "Failed to save project");
    }
  };

  const fetchBrandProduct = useCallback(async () => {
    try {
      const [brandRes, productRes] = await Promise.all([
        http.get(`brands`),
        http.get("products"),
      ]);
      setBrands(brandRes?.data?.brands);
      setProducts(productRes?.data?.product);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const { page, limit } = pagination;

      const projectRes = await http.get(
        `/projects?page=${page}&limit=${limit}`,
      );

      setAllProjects(projectRes.data.data);
      setTotalProjectCount(projectRes.data.total);
      setPagination((prev) => ({
        ...prev,
        page: projectRes.data.page,
        totalPages: projectRes.data.totalPages,
        total: projectRes.data.total,
        limit: projectRes.data.limit,
      }));
    } catch (error) {
      console.log(error);
    }
  }, [pagination.page, pagination.limit]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await http.delete(`/projects/${id}`);

      toast.success("Project deleted successfully");

      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete project");
    }
  };

  const handleEdit = (item) => {
    setEditId(item._id);

    setProject({
      title: item.title || "",
      slug: item.slug || "",
      shortDescription: item.shortDescription || "",
      description: item.description || "",

      bannerImage: item.bannerImage || "",
      bannerAlt: item.bannerAlt || "",

      category: item.category || "",

      clientName: item.clientName || "",

      location: item.location || "",

      status: item.status || "Completed",

      services: item.services || [],

      brands: (item.brands || []).map((brand) => brand._id),

      products: (item.products || []).map((product) => product._id),

      projectDuration: item.projectDuration || "",

      challenge: item.challenge || "",

      solution: item.solution || "",

      result: item.result || "",

      gallery: item.gallery || [],

      videos: item.videos || [],

      faq: item.faq || [],

      isPublished: item.isPublished !== undefined ? item.isPublished : true,

      seo: {
        metaTitle: item.seo?.metaTitle || "",
        metaDescription: item.seo?.metaDescription || "",
        metaTags: Array.isArray(item.seo?.metaTags)
          ? item.seo.metaTags.join(", ")
          : item.seo?.metaTags || "",
        schemaScript: item.seo?.schemaScript || "",
      },
    });

    setBannerImage(null);

    setImages({
      existing: item.gallery || [],
      new: [],
      removed: [],
    });

    setIsModalOpen(true);
  };

  useEffect(() => {
    fetchProjects();
  }, [pagination.page]);

  useEffect(() => {
    fetchBrandProduct();
  }, [fetchBrandProduct]);

  return (
    <div className="min-h-screen p-4 md:p-8 text-slate-200">
      <AdminHeader
        title="Projects"
        buttonText="Add Project"
        onClick={() => setIsModalOpen(true)}
      />

      <div className="flex flex-col md:flex-row w-full mb-8  gap-4">
        <StatCard
          title="Total Projects"
          count={totalProjectCount}
          icon={Package}
        />
      </div>

      {allProjects.length === 0 ? (
        <div className="p-8 rounded-2xl bg-white/5 bprder border-white/10 text-center text-slate-400 ">
          No Projects Found
        </div>
      ) : (
        <div className="space-y-4">
          {allProjects.map((item) => (
            <div
              key={item._id}
              className="       group
        flex
        flex-col
        lg:flex-row
        lg:items-center
        justify-between
        gap-4
        p-4
        rounded-2xl
        bg-slate-900/40
        border border-slate-800
        hover:border-slate-700
        hover:bg-slate-900/60
        transition-all"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0 ">
                <div
                  className="h-12 w-12 
                sm:h-16 sm:w-16
                rounded
                bg-whitep-2
                flex
                items-center
                justify-center
                shrink-0 
                "
                >
                  <img
                    src={GetImageUrl(item.bannerImage)}
                    className="max-h-full max-w-full object-contain "
                  />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm lg:text-base">
                    {item.title}
                  </h3>
                  <p className="text-sm text-slate-400 py-1">
                    {item?.status.toUpperCase()}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 ">
                <ActionButtons
                  onEdit={() => handleEdit(item)}
                  onDelete={() => handleDelete(item._id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {products.length > 0 && (
        <Pagination
          page={pagination?.page}
          totalPages={pagination?.totalPages}
          onPrevious={() =>
            setPagination((prev) => ({
              ...prev,
              page: prev.page - 1,
            }))
          }
          onNext={() =>
            setPagination((prev) => ({
              ...prev,
              page: prev.page + 1,
            }))
          }
        />
      )}

      <AnimatePresence>
        {isModalOpen && (
          <CreateProject
            editId={editId}
            project={project}
            bannerImage={bannerImage}
            setBannerImage={setBannerImage}
            images={images}
            setImages={setImages}
            brands={brands}
            products={products}
            handleSubmit={handleSubmit}
            closeModal={closeModal}
            updateProject={updateProject}
            updateSeo={updateSeo}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

const CreateProject = ({
  project,
  bannerImage,
  setBannerImage,
  editId,
  images,
  setImages,

  brands,
  products,

  handleSubmit,
  closeModal,

  updateProject,
  updateSeo,
}) => {
  const brandOptions = useMemo(() => {
    return brands.map((brand) => ({
      value: brand._id,
      label: brand.brandName,
    }));
  }, [brands]);

  const productOptions = useMemo(() => {
    return products.map((product) => ({
      value: product._id,
      label: product.title,
    }));
  }, [products]);
  const inputClass =
    "w-full border border-white/10 bg-white/5 text-white px-4 py-3 rounded-xl mt-2 outline-none focus:border-blue-500 transition";

  return (
    <motion.div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50">
      <motion.div
        className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
        w-[95%]
        h-[90vh]
        bg-[#0a0f1a]
        border border-white/10
        rounded-2xl
        shadow-2xl
        overflow-hidden
        flex flex-col"
      >
        <div
          className="flex items-center justify-between
          p-4 md:px-8
          border-b border-white/10
          bg-white/[0.03]"
        >
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-black text-white uppercase">
              {editId ? "Update Project" : "Create Project"}
            </h3>
            <p className="text-sm md:text-lg lg:text-xl">
              {editId
                ? "Update project details and media"
                : "Add project details and media"}
            </p>
          </div>

          <button
            type="button"
            onClick={closeModal}
            className="h-10 w-10 rounded-xl bg-white/5 hover:bg-red-500 text-slate-300 flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 md:p-8 space-y-6 overflow-y-auto flex-1"
        >
          <BasicProjectInfo
            project={project}
            updateProject={updateProject}
            inputClass={inputClass}
          />
          <div className="my-6 border-t border-white/30" />

          <BannerSection
            project={project}
            bannerAlt={project.bannerAlt}
            updateProject={updateProject}
            bannerImage={bannerImage}
            setBannerImage={setBannerImage}
          />
          <div className="my-6 border-t border-white/30" />

          <ProjectImageSection images={images} setImages={setImages} />
          <div className="my-6 border-t border-white/30" />

          <ProjectDescription project={project} updateProject={updateProject} />
          <div className="my-6 border-t border-white/30" />

          <BrandProductSection
            brands={brandOptions}
            products={productOptions}
            project={project}
            updateProject={updateProject}
          />
          <div className="my-6 border-t border-white/30" />

          <FaqSection
            project={project}
            updateProject={updateProject}
            inputClass={inputClass}
          />
          <div className="my-6 border-t border-white/30" />

          <ServiceSection
            project={project}
            updateProject={updateProject}
            inputClass={inputClass}
          />
          <div className="my-6 border-t border-white/30" />

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-300">
                Published
              </label>

              <select
                value={project.isPublished ? "yes" : "no"}
                onChange={(e) =>
                  updateProject("isPublished", e.target.value === "yes")
                }
                className={`${inputClass}`}
              >
                <option value="yes" className="bg-[#0a0f1a] text-white">
                  Yes
                </option>
                <option value="no" className="bg-[#0a0f1a] text-white">
                  No
                </option>
              </select>

              <p className="text-xs text-slate-400 mt-2">
                Choose whether this project should be visible on the website.
              </p>
            </div>
          </div>
          <div className="my-6 border-t border-white/30" />

          <VideoSection
            project={project}
            updateProject={updateProject}
            inputClass={inputClass}
          />
          <div className="my-6 border-t border-white/30" />

          <SeoSection
            project={project}
            updateSeo={updateSeo}
            inputClass={inputClass}
          />

          <div className="flex justify-end gap-3 pt-5 border-t border-white/10">
            <button
              type="button"
              onClick={closeModal}
              className="px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
            >
              {editId ? "Update Project" : "Create Project"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

